import { useTranslation } from 'next-i18next';
import * as yup from 'yup';

import { IAuth } from '@/modules/auth/types';
import { validatePhoneNumber } from '@/shared/lib/libphonenumber';
import { IGeneral } from '@/shared/types';

export const useSignup = () => {
  const { t } = useTranslation();

  const SignupFormValidationSchema: yup.SchemaOf<IAuth.ISignupSchema> = yup
    .object()
    .shape({
      email: yup
        .string()
        .required(t('this-field-is-required'))
        .email(t('Please enter a valid email')),
      // minimum of 8 characters and at least one number
      password: yup
        .string()
        .required(t('this-field-is-required'))
        .test(
          'password-strength',
          t('password-must-match-requirements'),
          function (value) {
            if (!value) {
              return false;
            }
            const hasMinLength = value?.length >= 8;
            const hasNumber = /\d/.test(value || '');
            const hasLetter = /[a-zA-Z]/.test(value || '');

            return hasMinLength && hasNumber && hasLetter;
          }
        ),
    });

  const completeSignupFormValidationSchema: yup.SchemaOf<
    Omit<
      IAuth.ICompleteSignupForm,
      'fieldName' | 'fieldType' | 'area' | 'areaUnit' | 'crop'
    >
  > = yup.object().shape({
    firstName: yup.string().required(t('this-field-is-required')),
    lastName: yup.string().required(t('this-field-is-required')),
    companyName: yup.string().required(t('this-field-is-required')),
    phoneNumber: yup
      .string()
      .required(t('this-field-is-required'))
      .test('isValidPhoneNumber', t('wrong-phone-number'), (value) =>
        validatePhoneNumber(value, true)
      ),
    jobTitle: yup.string().required(t('this-field-is-required')),
    country: yup.string().required(),
    currency: yup.string().required(),
    location: yup.string().required(),
    timeZone: yup.string().required(),
    noOfEmployees: yup.string().required(),
    landSize: yup.string().required(),
    cropCategories: yup
      .array()
      .of(yup.string().required())
      .min(1, t('this-field-is-required'))
      .required(),
    featuresOfInterest: yup
      .array()
      .of(yup.string().required())
      .min(1, t('this-field-is-required'))
      .required(),
  });

  const partialSignupFormValidationSchema = yup.object().shape({
    fieldName: yup.string().required(t('this-field-is-required')),
    fieldType: yup.object().required(t('this-field-is-required')),
    area: yup.string().required(t('this-field-is-required')),
    crop: yup.object().required(t('this-field-is-required')),
  });

  const defaultTimezones: Record<string, string> = {
    US: 'America/New_York',
    CA: 'America/Toronto',
    RU: 'Europe/Moscow',
    BR: 'America/Sao_Paulo',
    AU: 'Australia/Sydney',
    MX: 'America/Mexico_City',
    ID: 'Asia/Jakarta',
    CN: 'Asia/Shanghai',
    KZ: 'Asia/Almaty',
    CD: 'Africa/Kinshasa',
    GL: 'America/Nuuk',
    ES: 'Europe/Madrid',
    FR: 'Europe/Paris',
    PT: 'Europe/Lisbon',
    CL: 'America/Santiago',
    MN: 'Asia/Ulaanbaatar',
    AR: 'America/Argentina/Buenos_Aires',
    NZ: 'Pacific/Auckland',
    EC: 'America/Guayaquil',
    FM: 'Pacific/Chuuk',
  };

  const getCompanyCountryInfo = (country?: IGeneral.GetCountriesItemSchema) => {
    return {
      country: country?.isoCode,
      currency: country?.currency,
      location: country
        ? `${country.latitude},${country.longitude}`
        : undefined,
      timeZone: country
        ? defaultTimezones?.[country.isoCode] ?? country.timezones[0].zoneName
        : undefined,
    };
  };

  return {
    SignupFormValidationSchema,
    completeSignupFormValidationSchema,
    partialSignupFormValidationSchema,
    defaultTimezones,
    getCompanyCountryInfo,
  };
};
